import React, { useEffect, useMemo, useState } from 'react';
import {
    Typography,
    Grid,
    Box,
    useMediaQuery,
    useTheme,
} from '@material-ui/core';
import clsx from 'clsx';

import { Hero, ContentImage, Container, Card, List } from '~/components/ui';

const perfectFit = `${process.env.GATSBY_IMAGES}partnership/services/perfect-fit@3x.png`;
const clock = `${process.env.GATSBY_IMAGES}partnership/services/clock@3x.png`;
const packaging = `${process.env.GATSBY_IMAGES}partnership/services/packaging@3x.png`;
const collateral = `${process.env.GATSBY_IMAGES}partnership/services/collateral@3x.png`;
const doctorPortal = `${process.env.GATSBY_IMAGES}partnership/services/doctor-portal@3x.png`;
const support = `${process.env.GATSBY_IMAGES}partnership/services/support@3x.png`;

import styles from './styles';
import { scrollTo } from '~/utils/helpers';
import { JoinUsFormModal } from '~/components';
const growYourPractice = `${process.env.GATSBY_IMAGES}partnership/bestQualities/grow-your-practice.jpg`;
const cooperationResults = `${process.env.GATSBY_IMAGES}partnership/bestQualities/cooperation-results.jpg`;
const hereYourAlways = `${process.env.GATSBY_IMAGES}partnership/bestQualities/here-your-always.jpg`;
const growYourPracticeWebP = `${process.env.GATSBY_IMAGES}partnership/bestQualities/grow-your-practice.webp`;
const cooperationResultsWebP = `${process.env.GATSBY_IMAGES}partnership/bestQualities/cooperation-results.webp`;
const hereYourAlwaysWebP = `${process.env.GATSBY_IMAGES}partnership/bestQualities/here-your-always.webp`;

interface IPartnershipProps {
    location: any;
}
export const Partnership = ({}: IPartnershipProps) => {
    const classes = styles();
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('xs'));
    const [open, setOpen] = useState(false);
    const services = [
        { title: 'The Best Orthodontic Fit', image: perfectFit },
        { title: '24/7 Customer Service', image: clock },
        { title: 'Custom Packaging', image: packaging },
        { title: 'Customer Collateral', image: collateral },
        { title: 'Online Doctor Portal', image: doctorPortal },
        { title: 'Marketing Support', image: support },
    ];

    useEffect(() => {
        let hash = location.hash.replace('#', '');
        scrollTo(hash, 200);
    });

    const renderedServices = useMemo(() => {
        return services.map((service, index) => {
            return (
                <Grid
                    item
                    lg={4}
                    md={6}
                    sm={12}
                    xs={12}
                    className={classes.service}
                    key={index}
                >
                    <Card image={service.image} title={service.title} />
                </Grid>
            );
        });
    }, [services]);

    return (
        <>
            <JoinUsFormModal open={open} onClose={() => setOpen(false)} />
            <Container className={classes.heroContainer}>
                <Hero
                    rounded
                    centered
                    className={clsx(classes.hero1, classes.hero)}
                    containerClassName={classes.heroInner}
                >
                    <Typography
                        variant="h3"
                        component="h2"
                        className={classes.heroTitle}
                    >
                        Strengthening your <br /> patient relationships
                    </Typography>
                </Hero>
            </Container>
            <Container
                id="partnering-with-you"
                section
                className={classes.partnering}
            >
                <ContentImage
                    alt="growYourPractice"
                    imageSrc={growYourPractice}
                    imageSrcWebP={growYourPracticeWebP}
                    preTitle="PARTNERING WITH YOU"
                    title="Let’s grow your practice"
                    buttonText="Sign up"
                    onButtonClick={() => {
                        setOpen(true);
                    }}
                    content={
                        <Typography className={classes.text} variant="body1">
                            Becoming an Orthosnap provider enhances your patient
                            care with the clearest aligner in the industry,
                            improves your workflow with a doctor portal, and
                            expands your practice by attracting more patients
                            with the most affordable aligner on the market.
                        </Typography>
                    }
                />
            </Container>
            <Container id="patient-compliance" section>
                <ContentImage
                    alt="cooperationResults"
                    imageSrc={cooperationResults}
                    imageSrcWebP={cooperationResultsWebP}
                    preTitle="PATIENT COMPLIANCE"
                    title="Cooperation for great results"
                    buttonText="Verification Key Tips"
                    onButtonClick={() =>
                        window.open(
                            'https://help.orthosnap.com/en/articles/2842194-what-is-the-verification-step'
                        )
                    }
                    revert
                    content={
                        <Typography className={classes.text} variant="body1">
                            Positive outcomes are only obtainable through
                            patient compliance. A more effective solution means
                            a shorter treatment plan. There’s no choice between
                            price or performance – your patients get the best of
                            both worlds, and flexible payments, so there’s no
                            need to worry about the cost upfront.
                        </Typography>
                    }
                />
            </Container>
            <Container id="customer-service" section>
                <ContentImage
                    alt="hereYourAlways"
                    imageSrc={hereYourAlways}
                    imageSrcWebP={hereYourAlwaysWebP}
                    preTitle="CUSTOMER SERVICE"
                    title="Here for you, always"
                    buttonText="Speak with an Expert"
                    onButtonClick={() =>
                        window.open('https://meetings.hubspot.com/lrakowsky ')
                    }
                    content={
                        <Typography className={classes.text} variant="body1">
                            Orthosnap makes it as easy as possible to integrate
                            our treatment into the services you already offer.
                            We’re here to help, every step of the way. We
                            provide the full scope of what’s required.
                        </Typography>
                    }
                />
            </Container>
            <Container section>
                <Typography variant="subtitle1" className={classes.text}>
                    Grow your practice with these services:
                </Typography>
                <Grid container spacing={4} className={classes.services}>
                    {renderedServices}
                </Grid>
            </Container>
            <Container section className={classes.heroContainer}>
                <Hero
                    rounded
                    centered
                    className={clsx(classes.hero2, classes.hero)}
                    containerClassName={classes.heroInner}
                >
                    <Typography
                        variant="h3"
                        component="h2"
                        className={classes.heroTitle}
                    >
                        Better pricing for <br /> you and your patients
                    </Typography>
                </Hero>
            </Container>
            <Container section>
                <Typography
                    variant="h4"
                    component="h3"
                    className={clsx(
                        classes.text,
                        classes.centered,
                        classes.sectionTitle
                    )}
                >
                    Orthosnap vs. competitors
                </Typography>
                <Typography
                    variant="body1"
                    className={clsx(classes.text, classes.centered)}
                >
                    Unlike other aligner companies, our flexible ‘pay-as-you-go’{' '}
                    {!isXs && <br />}
                    model ensures you’re only paying for the aligners that are
                    used.
                </Typography>

                <Box className={classes.versus}>
                    <Box
                        className={clsx(classes.versusCard, classes.orthosnap)}
                    >
                        <Typography
                            variant="overline"
                            className={classes.versusTitle}
                        >
                            ORTHOSNAP
                        </Typography>
                        <List
                            items={[
                                'Pay-As-You-Go',
                                'Flexible, Step Pricing',
                                'Better Patient Compliance',
                            ]}
                        />
                    </Box>
                    <Box
                        className={clsx(classes.versusCard, classes.competitor)}
                    >
                        <Typography
                            variant="overline"
                            className={classes.versusTitle}
                        >
                            COMPETITORS
                        </Typography>
                        <List items={['Upfront costs', 'Tiered Pricing']} />
                    </Box>
                </Box>
            </Container>

            <Container grey section className={classes.pricingContainer}>
                <Typography
                    variant="h4"
                    component="h3"
                    className={clsx(
                        classes.text,
                        classes.centered,
                        classes.sectionTitle
                    )}
                >
                    Exactly what your patients need, {!isXs && <br />} for
                    exactly the right price.
                </Typography>
                <Typography
                    variant="body1"
                    className={clsx(classes.text, classes.centered)}
                >
                    Unlike other dental aligner companies which expect thousands
                    upfront, {!isXs && <br />} Orthosnap offers “Step Pricing” -
                    patients only pay for the aligners they use.
                </Typography>

                <Box className={classes.pricingBox}>
                    <Box className={classes.pricingImage} />
                    <List
                        items={[
                            'Keeps the cost down every step of the way.',
                            'Allows for a much larger range of complex cases.',
                            'Encourages patient compliance',
                        ]}
                    />
                </Box>
            </Container>
        </>
    );
};
